<template>
    <Modal size="md" @close="$emit('close')">
        <template #header>
            <div class="text-gray-600">Export to AWeber</div>
        </template>

        <template #body>
            <div class="p-6">
                <form @submit.prevent="exportToAWeber">
                    <SelectInput
                        v-model="state.account_id"
                        placeholder="Choose account"
                        :data="accounts"
                        label="Account"
                        required
                        :error="getError('account_id')"
                        :disabled="loading"
                        search
                        @blurred="v$.account_id.$touch()"
                    />

                    <SelectInput
                        v-model="state.list_id"
                        class="mt-6"
                        placeholder="Choose list"
                        :data="lists"
                        label="List"
                        required
                        :error="getError('list_id')"
                        :disabled="loading"
                        search
                        @blurred="v$.list_id.$touch()"
                    />
                </form>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between border-t border-gray-200 p-6">
                <AtomicButton color="white" size="md" @click="$emit('close')">
                    Cancel
                </AtomicButton>

                <AtomicButton
                    size="md"
                    color="yellow"
                    :disabled="loading || v$.$invalid"
                    @click="exportToAWeber"
                >
                    Export
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { PropType, reactive, ref } from 'vue';
import { IAWeber, IIntegration } from '@/types/Integrations/IIntegration';
import axios from 'axios';
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import ISelectable from '@/types/Atomic/ISelectable';

const emit = defineEmits(['close']);

const props = defineProps({
    integration: {
        required: true,
        type: Object as PropType<IIntegration<IAWeber>>,
    },
    templateId: {
        required: true,
        type: Number,
    },
    templateName: {
        required: true,
        type: String,
    },
});

const state = reactive({
    account_id: '',
    list_id: '',
});

const rules = {
    account_id: {
        required,
    },
    list_id: {
        required,
    },
};

const v$ = useVuelidate(rules, state);

const loading = ref(false);
const accounts = ref<ISelectable[]>([]);
const lists = ref<ISelectable[]>([]);

onMounted(() => {
    getAccounts();
});

watch(
    () => state.account_id,
    () => {
        getLists();
    }
);

const getAccounts = async () => {
    try {
        loading.value = true;
        const res = await axios.get(
            route('integrations.aweber.accounts.show', {
                integration: props.integration.id,
            })
        );

        if (res.data.success === true) {
            accounts.value = res.data.data;

            if (res.data.data.length > 0) {
                state.account_id = res.data.data[0].value;
                getLists();
            }
        }
    } catch (e) {
        return;
    } finally {
        loading.value = false;
    }
};

const getLists = async () => {
    try {
        loading.value = true;
        const res = await axios.get(
            route('integrations.aweber.lists.show', {
                integration: props.integration.id,
                account_id: state.account_id,
            })
        );

        if (res.data.success === true) {
            lists.value = res.data.data;

            if (res.data.data.length > 0) {
                state.list_id = res.data.data[0].value;
            }
        }
    } catch (e) {
        return;
    } finally {
        loading.value = false;
    }
};

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || '';
};

const exportToAWeber = async () => {
    loading.value = true;

    try {
        const response = await axios.post(
            route('templates.exports.aweber', {
                template: props.templateId,
            }),
            {
                account_id: state.account_id,
                list_id: state.list_id,
                integration_id: props.integration.id,
            }
        );

        if (response.data.success) {
            create({
                title: 'Successful export',
                text: 'Template has been successfully exported to AWeber.',
                type: 'success',
            });
            emit('close');

            return;
        }
        create({
            title: 'Unsuccessful export',
            text: 'Template has not been exported to AWeber.',
            type: 'error',
        });
    } catch (e) {
        return;
    } finally {
        loading.value = false;
    }
};
</script>
